import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { MONO } from '../recipes'
import { formatSpeechClock, type SpeechRingState, speechShare, wholeSeconds } from './voice-format'
import type { VoiceCounting } from './voice-types'

export interface SpeechRingProps {
  /**
   * The speech counted, and what it is counted towards. `speechMs` null: there is no count to
   * show — the one made here did not hold. `counting` 'time': the recorded time was counted
   * instead of speech, and the ring's words say so.
   */
  count: { speechMs: number | null; targetMs: number; counting?: VoiceCounting }
  state: SpeechRingState
  /** The microphone's level now, 0..1: the halo behind the ring breathes with it. None outside a recording. */
  level?: number | null
  /** The check of the room, while one is under way: drawn inside the ring, where nothing counts yet. */
  check?: SpeechRingCheck | null
  className?: string
}

/** The room check as the ring draws it. */
export interface SpeechRingCheck {
  /** How far it has come, 0..1. */
  share: number
  /** The room was found too loud: the sweep stands closed, in the warning tone. */
  loud?: boolean
}

/** The ring's own coordinates: the stroke on a circle of `RADIUS`, the scale's ticks outside it. */
const BOX = 240
const CENTRE = BOX / 2
const RADIUS = 102
const STROKE = 8
/** The room check's sweep runs inside the ring, clear of its stroke. */
const SWEEP_RADIUS = 88
/** The scale has a tick every this many seconds of speech. */
const TICK_EVERY_MS = 5_000
/** In the centre of a ring with no count: an instrument without a reading. */
const NO_COUNT = '-:--'

/** The stroke of the counted speech: copper while it counts, dimmed while nobody speaks. */
const STROKE_OF: Record<SpeechRingState, string> = {
  idle: 'stroke-primary',
  speaking: 'stroke-primary',
  waiting: 'stroke-muted-foreground',
  kept: 'stroke-primary',
  reached: 'stroke-primary',
  short: 'stroke-warning',
  weak: 'stroke-warning',
}

/** How far the halo swells with the level: it never leaves the ring's own box by much. */
const haloScale = (level: number): string =>
  (0.8 + Math.min(1, Math.max(0, level)) * 0.3).toFixed(3)

/**
 * The soft disc behind the ring, swelling with the live level. Under reduced motion it stands
 * still: the caption and the stroke already say whether a voice is heard.
 */
function LevelHalo({ level, speaking }: { level: number; speaking: boolean }) {
  return (
    <span
      aria-hidden
      style={{ transform: `scale(${haloScale(level)})` }}
      className={cn(
        'absolute inset-0 rounded-full blur-2xl transition-[transform,background-color] duration-150 ease-out motion-reduce:transform-none! motion-reduce:transition-none',
        speaking ? 'bg-primary/25' : 'bg-muted-foreground/10'
      )}
    />
  )
}

/** The scale around the ring: a tick for every five seconds of speech. */
function Scale({ targetMs }: { targetMs: number }) {
  const ticks = Math.max(1, Math.round(targetMs / TICK_EVERY_MS))
  return (
    <g strokeLinecap="round" className="stroke-border" strokeWidth={1.5}>
      {Array.from({ length: ticks }, (_, tick) => (
        <line
          key={tick}
          x1={CENTRE}
          x2={CENTRE}
          y1={3}
          y2={8}
          transform={`rotate(${(tick / ticks) * 360} ${CENTRE} ${CENTRE})`}
        />
      ))}
    </g>
  )
}

/** The mark of a full ring, drawn where the stroke closes: a copper seal with a check. */
function ReachedMark() {
  return (
    <g
      transform={`translate(${CENTRE} ${CENTRE - RADIUS})`}
      className="transition-opacity duration-300 starting:opacity-0 motion-reduce:transition-none"
    >
      <circle r={14} className="fill-primary stroke-background" strokeWidth={3} />
      <path
        d="M-5.5 0.5 L-1.5 4.5 L5.5 -3.5"
        pathLength={1}
        strokeDasharray="1 1"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="fill-none stroke-foreground transition-[stroke-dashoffset] duration-500 ease-out [stroke-dashoffset:0] starting:[stroke-dashoffset:1] motion-reduce:transition-none dark:stroke-background"
      />
    </g>
  )
}

/**
 * The room check, drawn inside the ring: a hairline that goes round once while the room is
 * listened to — the instrument settling before it measures. It is not the count: that stroke
 * stays empty until a voice is counted.
 */
function RoomSweep({ share, loud = false }: SpeechRingCheck) {
  const circle = { cx: CENTRE, cy: CENTRE, r: SWEEP_RADIUS }
  const drawn = Math.min(1, Math.max(0, share)) * 100
  return (
    <g
      data-room-check={loud ? 'loud' : 'measuring'}
      className="transition-opacity duration-300 starting:opacity-0 motion-reduce:transition-none"
    >
      <circle {...circle} strokeWidth={1} className="fill-none stroke-border" />
      {drawn > 0 && (
        <circle
          {...circle}
          strokeWidth={2.5}
          pathLength={100}
          strokeDasharray={`${drawn} 100`}
          strokeLinecap="round"
          transform={`rotate(-90 ${CENTRE} ${CENTRE})`}
          className={cn(
            'fill-none transition-[stroke-dasharray,stroke] duration-100 ease-linear motion-reduce:transition-none',
            loud ? 'stroke-warning' : 'stroke-muted-foreground'
          )}
        />
      )}
    </g>
  )
}

interface DialProps {
  share: number
  targetMs: number
  state: SpeechRingState
  check: SpeechRingCheck | null
}

/**
 * The dial: the scale, the track, the counted speech drawn from twelve o'clock and its seal —
 * and, while the room is checked, the sweep inside it.
 */
function Dial({ share, targetMs, state, check }: DialProps) {
  const circle = { cx: CENTRE, cy: CENTRE, r: RADIUS, strokeWidth: STROKE }
  return (
    <svg viewBox={`0 0 ${BOX} ${BOX}`} aria-hidden className="absolute inset-0 size-full">
      <Scale targetMs={targetMs} />
      <circle {...circle} className="fill-none stroke-border" />
      {check && <RoomSweep {...check} />}
      {share > 0 && (
        <circle
          {...circle}
          pathLength={100}
          strokeDasharray={`${share * 100} 100`}
          strokeLinecap="round"
          transform={`rotate(-90 ${CENTRE} ${CENTRE})`}
          // One tick of the count, drawn at an even pace: the ring glides, it does not step.
          className={cn(
            'fill-none transition-[stroke-dasharray,stroke] duration-100 ease-linear motion-reduce:transition-none',
            STROKE_OF[state]
          )}
        />
      )}
      {state === 'reached' && <ReachedMark />}
    </svg>
  )
}

/** The count in words, for assistive technology: of speech, or of recorded time. */
function useRingWords({ speechMs, targetMs, counting = 'speech' }: SpeechRingProps['count']) {
  const { t } = useTranslation()
  const byTime = counting === 'time'
  const label = byTime ? 'humint.voice.sample.ring.labelByTime' : 'humint.voice.sample.ring.label'
  // No count: the ring says what the server found instead of a number it did not find.
  if (speechMs === null) return { label: t(label), value: t('humint.voice.sample.state.no_speech') }
  const value = byTime ? 'humint.voice.sample.ring.valueByTime' : 'humint.voice.sample.ring.value'
  const counted = formatSpeechClock(speechMs)
  return { label: t(label), value: t(value, { counted, target: formatSpeechClock(targetMs) }) }
}

/**
 * The speech ring of a voice sample (ADR-0062): how much of the subject's speech has been
 * counted, of what is asked for. It moves only while a voice is heard — the stroke dims the
 * moment the subject pauses — and closes with a check only where its caller says the sample
 * holds everything (`reached`). The count stands in its centre in mono, so the ring never carries
 * the state alone. A progress bar to assistive technology, with the count in words. While the
 * room is checked (`check`) a hairline sweeps inside it and the count stands back: nothing is
 * counted yet.
 *
 * It is 13 rem unless `className` sizes it; the count is set in the ring's own width, so it keeps
 * its proportions at any size.
 */
export function SpeechRing({
  count,
  state,
  level = null,
  check = null,
  className,
}: SpeechRingProps) {
  const { speechMs, targetMs } = count
  const words = useRingWords(count)
  const seconds = speechMs === null ? undefined : wholeSeconds(speechMs)
  // No reading, or none yet: the count stands back.
  const standsBack = speechMs === null || check !== null
  return (
    <div
      role="progressbar"
      aria-label={words.label}
      aria-valuemin={0}
      aria-valuemax={wholeSeconds(targetMs)}
      aria-valuenow={seconds === undefined ? undefined : Math.min(seconds, wholeSeconds(targetMs))}
      aria-valuetext={words.value}
      className={cn(
        'relative flex size-52 shrink-0 items-center justify-center [container-type:inline-size]',
        className
      )}
    >
      {level !== null && <LevelHalo level={level} speaking={state === 'speaking'} />}
      <Dial
        share={speechMs === null ? 0 : speechShare(speechMs, targetMs)}
        targetMs={targetMs}
        state={state}
        check={check}
      />
      <span aria-hidden className="relative flex flex-col items-center gap-[4cqi]">
        <span
          className={cn(
            MONO,
            'text-[length:29cqi] leading-none tracking-tight transition-colors duration-300 motion-reduce:transition-none',
            speechMs === null ? 'font-normal' : 'font-semibold',
            standsBack && 'text-muted-foreground'
          )}
        >
          {speechMs === null ? NO_COUNT : formatSpeechClock(speechMs)}
        </span>
        {/* Never under 11 px, however small the ring. */}
        <span
          className={cn(
            MONO,
            'text-[length:max(0.6875rem,7.7cqi)] leading-none text-muted-foreground'
          )}
        >
          / {formatSpeechClock(targetMs)}
        </span>
      </span>
    </div>
  )
}
